import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { personService } from '@shared/services/personService';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import useIbanValidation from '@shared/hooks/useIbanValidation';

export const usePaymentForm = (person) => {
    const { t } = useTranslation();
    const { validateIBAN } = useIbanValidation();
    const [paymentData, setPaymentData] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(true);

    const paymentValidationSchema = yup.object({
        iban: yup.string()
            .required(t('validation.person.payment.ibanRequired'))
            .test('iban-format', t('validation.person.payment.ibanInvalid'), (value, context) => {
                if (!value) return true; // Let required handle empty values
                const bic = context.parent.bic;
                const result = validateIBAN(value, bic);
                return result.isValid;
            }),
        description: yup.string()
            .max(80, t('validation.person.payment.noteLength')),
        bic: yup.string(),
        bankName: yup.string(),
        isActive: yup.boolean()
            .required(t('validation.person.payment.isActiveRequired'))
    });

    const {
        register: paymentFormRegister,
        formState: { errors: paymentErrors },
        setValue: setPaymentValue,
        watch,
        handleSubmit,
        reset
    } = useForm({
        resolver: yupResolver(paymentValidationSchema),
        defaultValues: {
            bankId: '',
            bic: '',
            bankName: '',
            iban: '',
            description: '',
            isActive: true
        }
    });

    const isActiveChecked = watch('isActive');

    const fetchBanks = async () => {
        try {
            const response = await personService.banksSelect();
            setBanks(response || []);
        } catch (error) {
            console.error('Error fetching banks:', error);
            setBanks([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPayments = async () => {
        try {
            if (person?.id) {
                const payments = await personService.getPersonPayments(person.id);
                setPaymentData(payments);
            }
        } catch (error) {
            console.error('Error fetching payments:', error);
            notifyError(t('persons.payment.fetchError'));
        }
    };

    useEffect(() => {
        fetchBanks();
        fetchPayments();
    }, []);

    useEffect(() => {
        if (!loading && banks.length > 0) {
            fetchPayments();
        }
    }, [loading, banks, person?.id]);

    const handleEdit = (payment) => {
        
        setIsEditing(true);
        setEditingPayment(payment);
        setPaymentValue('bankId', payment.bank.bankId);
        setPaymentValue('iban', payment.iban);
        setPaymentValue('description', payment.description);
        setPaymentValue('isActive', payment.isActive);
        setPaymentValue('bic', payment.bank.bankCode);
        setPaymentValue('bankName', payment.bank.name);
    };

    const submitPaymentForm = async (formData) => {
        try {
            if (!person?.id) {
                notifyError(t('persons.payment.personIdRequired'));
                return false;
            }
            const payload = {
                personId: person.id,
                bankId: formData.bankId,
                iban: formData.iban,
                description: formData.description,
                isActive: formData.isActive
            };

            if (isEditing && editingPayment) {
                await personService.updatePersonPayment(person.id, editingPayment.id, payload);
                notifySuccess(t('persons.payment.updateSuccess'));
            } else {
                await personService.addPersonPayment(person.id, payload);
                notifySuccess(t('persons.payment.addSuccess'));
            }

            reset({
                bic: '',
                bankName: '',
                bankId: '',
                iban: '',
                description: '',
                isActive: true
            });
            setIsEditing(false);
            setEditingPayment(null);
            await fetchPayments();
            return true;
        } catch (error) {
            console.error('Payment operation failed:', error);
            notifyError(isEditing
                ? t('persons.payment.updateError')
                : t('persons.payment.addError')
            );
            return false;
        }
    };

    const handleDelete = async (paymentId) => {
        try {
            await personService.deletePersonPayment(paymentId);
            notifySuccess(t('persons.payment.deleteSuccess'));
            await fetchPayments();
        } catch (error) {
            notifyError(t('persons.payment.deleteError'));
        }
    };

    const handleCancel = () => {
        reset({
            bic: '',
            bankName: '',
            bankId: '',
            iban: '',
            description: '',
            isActive: true
        });
        setIsEditing(false);
        setEditingPayment(null);
    };

    return {
        paymentData,
        paymentFormRegister,
        paymentErrors,
        setPaymentValue,
        handleEdit,
        handleDelete,
        submitPaymentForm,
        isEditing,
        handleSubmit,
        handleCancel,
        isActiveChecked,
        watch,
        loading,
        banks
    };
}; 